import { ApiError, USE_MOCKS, api, descargarArchivo, mockDelay } from "@/lib/api"
import type { Filing, FilingDetail } from "@/lib/types"
import type { Declaracion } from "@/routes/accountant/declaracion"
import type {
  FilingDetailRow,
  FilingInboxRow,
  FirmaRow,
  ObservacionRow,
} from "@/routes/accountant/mapping"
import {
  etiquetaLibro,
  filingDetailFromApi,
  filingFromApi,
  mensajeDeConflicto,
  observeFromApi,
  observeToApi,
  signFromApi,
  signToApi,
} from "@/routes/accountant/mapping"

/**
 * Consola del contador (cross-tenant). Modulo `filings` del backend.
 *
 *   GET  /api/filings/inbox              (alcance = cartera del contador)
 *   GET  /api/filings/{period_id}          (detalle del periodo abierto)
 *   POST /api/filings/{period_id}/sign     { motivo, forzar_advertencias }
 *   POST /api/filings/{period_id}/observe  { motivo, detalle? }
 *   GET  /api/filings/{period_id}/export   ?formato=json|ple|fv621
 *
 * EL TENANT NO ES EL ACTIVO. `/filings/inbox` es el unico endpoint cross-tenant
 * del sistema: devuelve periodos de TODA la cartera. Las tres rutas de escritura
 * si piden el tenant del periodo, asi que cada una manda `filing.tenantId` en el
 * header. Usar el tenant activo daria 403 en toda fila que no sea la suya, y
 * seria un 403 desconcertante: el contador si tiene permiso, solo que sobre otro
 * tenant.
 *
 * Este archivo NO traduce: delega la forma a `mapping.ts`, igual que en
 * `masters`. `USE_MOCKS` sigue intacto.
 *
 * DOS CONSULTAS, A PROPOSITO. La bandeja (`/inbox`) es cross-tenant y no toca
 * los asientos: no puede traer el cuadre por libro ni el diff sin recorrer la
 * cartera entera. El detalle (`/filings/{id}`) es la segunda consulta y sale
 * SOLO por el periodo que el contador abrio. Ese es el reparto: la lista barata
 * y el detalle caro bajo demanda.
 *
 * EL DETALLE SI PIDE TENANT. `/inbox` es la unica ruta cross-tenant; el detalle,
 * como las tres de escritura, va con el `tenantId` del periodo. Mandar el tenant
 * activo daria un 403 desconcertante en toda fila ajena.
 */

const MOCK_FILINGS: Filing[] = [
  {
    id: "fil-001",
    tenantId: "t-001",
    tenantName: "Andina Servicios SAC",
    ruc: "20512223334",
    period: "2026-07",
    form: "FV 621 - IGV Renta mensual",
    dueDate: "2026-08-14",
    status: "observado",
    preparedBy: "motor",
    igvDebito: 21600,
    igvCredito: 9748.8,
    igvAPagar: 11851.2,
    pagoACuenta: 1800,
    exceptionsCount: 3,
    balanced: true,
    exceptions: [
      {
        id: "ex-1",
        severity: "alta",
        title: "Detraccion sin constancia de deposito",
        detail:
          "F003-0000781 de Consultora Talento Plus SAC (12%, S/ 2,124). Sin la constancia el credito fiscal no se puede usar en julio.",
        documentRef: "F003-0000781",
        amount: 2124,
        source: "regla",
        resolved: false,
      },
      {
        id: "ex-2",
        severity: "media",
        title: "Gasto de representacion en el limite",
        detail:
          "Acumulado del ejercicio en 0.48% de ingresos brutos (limite 0.5%). El exceso se adiciona en TAX segun regla TR-007.",
        documentRef: "F100-0001177",
        amount: 980,
        source: "llm",
        resolved: false,
      },
      {
        id: "ex-3",
        severity: "baja",
        title: "Proveedor nuevo clasificado por modelo",
        detail:
          "Servicios Cloud Andina SAC (RUC 20512345678) clasificado en 63911 con 62% de confianza y confirmado por un humano el 03/07.",
        documentRef: "F001-0004821",
        amount: 4956,
        source: "llm",
        resolved: true,
      },
    ],
    diff: [
      { concept: "Debito fiscal", previous: 19800, current: 21600, delta: 1800, deltaPct: 0.0909 },
      {
        concept: "Credito fiscal",
        previous: 11200,
        current: 9748.8,
        delta: -1451.2,
        deltaPct: -0.1296,
        note: "Cae por la detraccion pendiente de deposito.",
      },
      { concept: "IGV a pagar", previous: 8600, current: 11851.2, delta: 3251.2, deltaPct: 0.3781 },
      { concept: "Pago a cuenta renta", previous: 1650, current: 1800, delta: 150, deltaPct: 0.0909 },
      {
        concept: "Comprobantes de compra",
        previous: 42,
        current: 38,
        delta: -4,
        deltaPct: -0.0952,
      },
    ],
  },
  {
    id: "fil-002",
    tenantId: "t-002",
    tenantName: "Nexo Digital SAC",
    ruc: "20601112223",
    period: "2026-07",
    form: "FV 621 - IGV Renta mensual",
    dueDate: "2026-08-18",
    status: "listo_para_revision",
    preparedBy: "motor",
    igvDebito: 34200,
    igvCredito: 18940,
    igvAPagar: 15260,
    pagoACuenta: 2850,
    exceptionsCount: 1,
    balanced: true,
    exceptions: [
      {
        id: "ex-4",
        severity: "media",
        title: "Nota de credito sin documento de origen enlazado",
        detail:
          "FC01-0000045 referencia F001-0004821 pero llego por correo antes que la factura. Se enlazo por clave fiscal.",
        documentRef: "FC01-0000045",
        amount: -495.6,
        source: "regla",
        resolved: true,
      },
    ],
    diff: [
      { concept: "Debito fiscal", previous: 33800, current: 34200, delta: 400, deltaPct: 0.0118 },
      { concept: "Credito fiscal", previous: 17500, current: 18940, delta: 1440, deltaPct: 0.0823 },
      { concept: "IGV a pagar", previous: 16300, current: 15260, delta: -1040, deltaPct: -0.0638 },
      { concept: "Pago a cuenta renta", previous: 2810, current: 2850, delta: 40, deltaPct: 0.0142 },
    ],
  },
  {
    id: "fil-003",
    tenantId: "t-003",
    tenantName: "Corporacion Vela SAC",
    ruc: "20554443332",
    period: "2026-07",
    form: "PLE 8.1 - Registro de compras",
    dueDate: "2026-08-20",
    status: "listo_para_revision",
    preparedBy: "Rosa Quispe",
    igvDebito: 0,
    igvCredito: 26410.5,
    igvAPagar: 0,
    pagoACuenta: 0,
    exceptionsCount: 2,
    balanced: true,
    exceptions: [
      {
        id: "ex-5",
        severity: "alta",
        title: "Dos comprobantes sin sustento de bancarizacion",
        detail:
          "Pagos en efectivo mayores a S/ 2,000 (F007-0000064 y F001-0000992). Sin bancarizacion se pierde el credito fiscal y la deduccion.",
        amount: 11564,
        source: "regla",
        resolved: false,
      },
      {
        id: "ex-6",
        severity: "baja",
        title: "Tipo de cambio tomado del cierre y no de la fecha de emision",
        detail: "E001-0000233 (USD 3,000). Diferencia de S/ 45 contra el t.c. publicado.",
        documentRef: "E001-0000233",
        amount: 45,
        source: "humano",
        resolved: true,
      },
    ],
    diff: [
      { concept: "Comprobantes", previous: 61, current: 58, delta: -3, deltaPct: -0.0492 },
      { concept: "Base imponible", previous: 142000, current: 146725, delta: 4725, deltaPct: 0.0333 },
      { concept: "Credito fiscal", previous: 25560, current: 26410.5, delta: 850.5, deltaPct: 0.0333 },
    ],
  },
  {
    id: "fil-004",
    tenantId: "t-001",
    tenantName: "Andina Servicios SAC",
    ruc: "20512223334",
    period: "2026-07",
    form: "PLE 14.1 - Registro de ventas",
    dueDate: "2026-08-14",
    status: "firmado",
    preparedBy: "motor",
    signedBy: "Rosa Quispe",
    igvDebito: 21600,
    igvCredito: 0,
    igvAPagar: 21600,
    pagoACuenta: 0,
    exceptionsCount: 0,
    balanced: true,
    exceptions: [],
    diff: [
      { concept: "Comprobantes emitidos", previous: 18, current: 21, delta: 3, deltaPct: 0.1667 },
      { concept: "Base imponible", previous: 110000, current: 120000, delta: 10000, deltaPct: 0.0909 },
      { concept: "IGV", previous: 19800, current: 21600, delta: 1800, deltaPct: 0.0909 },
    ],
  },
  {
    id: "fil-005",
    tenantId: "t-002",
    tenantName: "Nexo Digital SAC",
    ruc: "20601112223",
    period: "2026-08",
    form: "FV 621 - IGV Renta mensual",
    dueDate: "2026-09-16",
    status: "en_preparacion",
    preparedBy: "motor",
    igvDebito: 12800,
    igvCredito: 6210,
    igvAPagar: 6590,
    pagoACuenta: 1050,
    exceptionsCount: 4,
    balanced: false,
    exceptions: [
      {
        id: "ex-7",
        severity: "alta",
        title: "El periodo aun no cuadra",
        detail:
          "El balance de comprobacion al 19/08 tiene una diferencia de S/ 340.00. No se puede firmar hasta cuadrar.",
        amount: 340,
        source: "regla",
        resolved: false,
      },
      {
        id: "ex-8",
        severity: "media",
        title: "12 comprobantes pendientes en la cola de revision",
        detail: "Baja confianza o proveedor nuevo. Se resuelven desde la cola de clasificacion.",
        source: "llm",
        resolved: false,
      },
      {
        id: "ex-9",
        severity: "baja",
        title: "Extracto bancario sin conciliar",
        detail: "Faltan 6 movimientos por conciliar contra el libro.",
        source: "regla",
        resolved: false,
      },
      {
        id: "ex-10",
        severity: "baja",
        title: "Sin descarga de SUNAT desde el 17/08",
        detail: "El ultimo pipeline_run de ingesta fallo por credenciales SOL vencidas.",
        source: "regla",
        resolved: false,
      },
    ],
    diff: [
      { concept: "Debito fiscal", previous: 34200, current: 12800, delta: -21400, deltaPct: -0.6257, note: "Mes en curso: parcial." },
      { concept: "Credito fiscal", previous: 18940, current: 6210, delta: -12730, deltaPct: -0.6721 },
      { concept: "IGV a pagar", previous: 15260, current: 6590, delta: -8670, deltaPct: -0.5682 },
    ],
  },
]

export async function fetchFilings(): Promise<Filing[]> {
  if (USE_MOCKS) return mockDelay(MOCK_FILINGS)
  // Sin `tenant_id`: el endpoint recorre el alcance completo del usuario. Es el
  // unico sitio donde NO acotar por tenant es lo correcto.
  const rows = await api.get<FilingInboxRow[]>("/filings/inbox")
  return rows.map(filingFromApi)
}

/**
 * Detalle del periodo: las excepciones reales, el cuadre por libro y el diff.
 *
 * Se llama al abrir el dialogo, no al cargar la bandeja: son N consultas caras
 * (recorren `journal_line` del periodo y del anterior) y el contador abre una
 * fila a la vez.
 *
 * El mock se DERIVA de la fila de la bandeja en vez de escribirse aparte: dos
 * juegos de datos falsos que dicen cosas distintas sobre el mismo periodo es
 * exactamente como se cuela un defecto que solo aparece con el backend real.
 */
export async function fetchFilingDetail(id: string, tenantId?: string): Promise<FilingDetail> {
  if (USE_MOCKS) {
    const mock = MOCK_FILINGS.find((f) => f.id === id) ?? MOCK_FILINGS[0]
    return mockDelay<FilingDetail>({
      period: mock.period,
      entityName: mock.tenantName,
      taxId: mock.ruc,
      country: "PE",
      status: mock.status,
      preparedBy: mock.preparedBy || null,
      signedBy: mock.signedBy ?? null,
      bookTotals: ["statutory", "tax", "management"].map((tipo) => ({
        book: tipo,
        label: etiquetaLibro(tipo),
        debit: mock.igvDebito,
        credit: mock.balanced ? mock.igvDebito : mock.igvDebito - 120,
        entries: 12,
        balanced: mock.balanced,
      })),
      documentCounts: [
        { status: "contabilizado", count: 34 },
        { status: "pendiente", count: mock.exceptions.length },
      ],
      balanced: mock.balanced,
      hasEntries: true,
      exceptions: mock.exceptions,
      diff: mock.diff,
    })
  }
  const row = await api.get<FilingDetailRow>(`/filings/${id}`, { tenantId })
  return filingDetailFromApi(row)
}

/**
 * Firma del periodo.
 *
 * El 409 de este endpoint es el unico del sistema cuyo `detail` es un OBJETO
 * ({mensaje, excepciones}) en vez de texto, y `writeErrors.ts` solo sabe mostrar
 * texto: sin aplanarlo, el usuario ve "el periodo puede estar cerrado" mientras
 * el servidor le esta diciendo exactamente que le falta. Se re-lanza el mismo
 * `ApiError` con el detalle ya legible, conservando estado y `request_id`.
 */
export async function signFiling(id: string, reason: string, tenantId?: string) {
  if (USE_MOCKS) return mockDelay({ period: "", status: "firmado" as const, signedBy: "", acceptedWarnings: 0 })
  try {
    const row = await api.post<FirmaRow>(`/filings/${id}/sign`, signToApi(reason), { tenantId })
    return signFromApi(row)
  } catch (error) {
    if (error instanceof ApiError) {
      const mensaje = mensajeDeConflicto(error.detail)
      if (mensaje) throw new ApiError(error.status, mensaje, mensaje, error.requestId)
    }
    throw error
  }
}

export async function observeFiling(id: string, reason: string, tenantId?: string) {
  if (USE_MOCKS) return mockDelay({ status: "en_preparacion" as const, observedBy: "" })
  const row = await api.post<ObservacionRow>(
    `/filings/${id}/observe`,
    observeToApi(reason),
    { tenantId },
  )
  return observeFromApi(row)
}

/**
 * Exportar.
 *
 * `GET /filings/{id}/export` ARMA las declaraciones del periodo con el rulepack
 * del pais: casillas, excepciones y los archivos oficiales. Antes devolvia solo
 * un «listo / no listo» porque no habia con que armarlas —faltaba la
 * implementacion de `LedgerQuery`, que ahora vive en `filings/libro.py`—.
 *
 * SIN MOCK, y a diferencia del resto de este archivo. Un mock que devuelve una
 * declaracion plausible es lo unico que no se puede permitir en esta pantalla:
 * lo que se ve aca se firma.
 */
export async function fetchDeclaracion(id: string, tenantId?: string): Promise<Declaracion> {
  return api.get<Declaracion>(`/filings/${id}/export`, { tenantId })
}

/**
 * Baja el archivo oficial —el TXT del PLE— para cargarlo en el portal.
 *
 * NO se puede hacer con un `<a href>`: la petición lleva el token de Firebase y
 * la cabecera del tenant, y un enlace no los lleva. Sin eso el backend responde
 * 401 y el navegador se descarga la pagina de error con extension .TXT, que es
 * peor que fallar — el archivo existe, pesa, y el validador de SUNAT lo rechaza
 * sin que nadie entienda por que.
 *
 * El nombre sale del `Content-Disposition`: lo construye el backend con el RUC,
 * el periodo y los indicadores del PLE, y ese nombre ES parte del formato.
 */
export async function descargarArchivoDeclaracion(
  id: string,
  filingCode: string,
  nombre: string,
  /** El tenant del período. La bandeja es cross-tenant; ver `descargarArchivo`. */
  tenantId?: string,
): Promise<void> {
  const query = new URLSearchParams({ filing_code: filingCode, nombre }).toString()
  return descargarArchivo(`/filings/${id}/archivo?${query}`, nombre, tenantId)
}
